import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Tooltip,
  IconButton,
  ThemeProvider,
  createTheme,
  Pagination,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import InboxIcon from "@mui/icons-material/Inbox";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import listStyles from "./AllEnquiries.module.scss";
import styles from "./Universities.module.scss";
import { bulkApproveUniversities, getUniversities, updateUniversity } from "../Components/enquiryProfileApi";

const theme = createTheme({
  palette: { primary: { main: "#5e72e4" }, background: { default: "#f8f9fe" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", h4: { fontWeight: 600 }, button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
});

const LIST_ID = "admin-approval";
const ITEMS_PER_PAGE = 15;

function statusBadgeClass(status) {
  if (status === "approved") return styles.badgeApproved;
  if (status === "rejected") return styles.badgeRejected;
  return styles.badgePending;
}

export default function UniversitiesApprovalPage() {
  const [universities, setUniversities] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editNameById, setEditNameById] = useState({});
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const data = await getUniversities({ status: "pending", includeUsage: true });
      setUniversities(Array.isArray(data?.universities) ? data.universities : []);
    } catch (error) {
      toast.error(error.message || "Failed to load pending universities.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const searchLower = (debouncedSearch || "").trim().toLowerCase();
  const filteredUniversities = searchLower
    ? universities.filter((u) => (u.name || "").toLowerCase().includes(searchLower))
    : universities;

  const totalPages = Math.max(1, Math.ceil(filteredUniversities.length / ITEMS_PER_PAGE));
  const start = (page - 1) * ITEMS_PER_PAGE;
  const paginated = filteredUniversities.slice(start, start + ITEMS_PER_PAGE);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const onApprove = async (id) => {
    try {
      await updateUniversity(id, { status: "approved" });
      toast.success("University approved.");
      await load();
    } catch (error) {
      toast.error(error.message || "Approve failed.");
    }
  };

  const onReject = async (id) => {
    try {
      await updateUniversity(id, { status: "rejected" });
      toast.success("University rejected.");
      await load();
    } catch (error) {
      toast.error(error.message || "Reject failed.");
    }
  };

  const onSaveName = async (id) => {
    const name = (editNameById[id] || "").trim();
    if (!name) {
      toast.warning("University name cannot be empty.");
      return;
    }
    try {
      await updateUniversity(id, { name });
      toast.success("University name updated.");
      await load();
    } catch (error) {
      toast.error(error.message || "Update failed.");
    }
  };

  const onBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Select at least one university for bulk approve.");
      return;
    }
    try {
      await bulkApproveUniversities(selectedIds);
      toast.success("Selected universities approved.");
      setSelectedIds([]);
      await load();
    } catch (error) {
      toast.error(error.message || "Bulk approve failed.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Grid container spacing={0}>
          <Grid item xs={12} className={listStyles.mainContent}>
            <Paper elevation={0} className={listStyles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h4" className={listStyles.pageTitle}>
                      University Admin Approval
                    </Typography>
                    <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Review and approve pending universities.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={listStyles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search university name…"
                />
                <Box className={listStyles.headerActions}>
                  <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                  <Button onClick={onBulkApprove}>Bulk Approve</Button>
                </Box>
              </Box>

              {filteredUniversities.length === 0 && (
                <Box className={listStyles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No pending universities
                  </Typography>
                </Box>
              )}

              {filteredUniversities.length > 0 && (
                <>
                  {viewMode === "cards" ? (
                    <Grid container spacing={2} className={listStyles.cardsGrid}>
                      {paginated.map((uni) => (
                        <Grid item xs={12} sm={6} md={4} key={uni.id}>
                          <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardHeader
                              avatar={
                                uni.logo_url ? (
                                  <Avatar src={uni.logo_url} alt={uni.name} sx={{ width: 48, height: 48 }} />
                                ) : (
                                  <Avatar sx={{ width: 48, height: 48, bgcolor: "grey.400" }}>N/A</Avatar>
                                )
                              }
                              title={uni.name}
                              subheader={<span className={statusBadgeClass(uni.status)}>{uni.status}</span>}
                            />
                            <CardContent>
                              <Typography variant="body2">
                                Created: {String(uni.created_at || "").slice(0, 10)}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                <Button size="sm" variant="outline-success" onClick={() => onApprove(uni.id)}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline-danger" onClick={() => onReject(uni.id)}>
                                  Reject
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th />
                            <th>Name</th>
                            <th>Status</th>
                            <th>Created Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginated.map((uni) => (
                            <tr key={uni.id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedSet.has(uni.id)}
                                  onChange={() => toggleSelect(uni.id)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  value={editNameById[uni.id] ?? uni.name}
                                  onChange={(e) =>
                                    setEditNameById((prev) => ({ ...prev, [uni.id]: e.target.value }))
                                  }
                                />
                              </td>
                              <td>
                                <Badge bg={uni.status === "approved" ? "success" : uni.status === "rejected" ? "danger" : "warning"} className="text-capitalize">
                                  {uni.status}
                                </Badge>
                              </td>
                              <td>{String(uni.created_at || "").slice(0, 10)}</td>
                              <td>
                                <Button size="sm" variant="outline-success" onClick={() => onApprove(uni.id)}>
                                  Approve
                                </Button>{" "}
                                <Button size="sm" variant="outline-danger" onClick={() => onReject(uni.id)}>
                                  Reject
                                </Button>{" "}
                                <Button size="sm" variant="outline-primary" onClick={() => onSaveName(uni.id)}>
                                  Save Name
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Box className={listStyles.pagination}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {paginated.length} of {filteredUniversities.length} pending universities
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, p) => setPage(p)}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}
